import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LockKeyhole, Trophy, Eye, EyeOff, LogIn } from "lucide-react";

function AdminLogin() {
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!password) {
      setError("Please enter your password.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await fetch(
  `${import.meta.env.VITE_API_URL}/api/admin/login`,
  {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({
      id: 1,
      password,
    }),
  }
);

      const data = await response.json();

      if (!response.ok) {
        setError(data.message || "Invalid password.");
        return;
      }

      // Save admin login
      localStorage.setItem("admin_id", "1");
      localStorage.setItem("admin_token", data.token);

      navigate("/admin-player");
    } catch (error) {
      console.error(error);
      setError("Unable to connect to the server.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-gradient-to-br from-[#120505] via-[#2b0808] to-black p-4">
      <div className="w-full max-w-md">

        {/* Logo */}
        <div className="mb-8 text-center">
          <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-red-600 to-orange-500 shadow-xl shadow-red-900/40">
            <Trophy className="h-10 w-10 text-white" />
          </div>

          <p className="mt-5 text-sm font-bold uppercase tracking-[0.3em] text-orange-400">
            BPL Season 2
          </p>

          <h1 className="mt-2 text-3xl font-black text-white">
            Admin Login
          </h1>

          <p className="mt-2 text-sm text-gray-400">
            Badgaw Premier League
          </p>
        </div>

        {/* Login Card */}
        <div className="rounded-3xl border border-white/10 bg-white/[0.06] p-6 shadow-2xl backdrop-blur-xl sm:p-8">

          <form onSubmit={handleLogin}>

            {/* Admin ID */}
            <div className="mb-5">
              <label className="mb-2 block text-sm font-semibold text-gray-300">
                Admin ID
              </label>

              <div className="flex h-12 items-center rounded-xl border border-white/10 bg-black/30 px-4 text-gray-400">
                <span className="font-semibold">1</span>
              </div>

              <p className="mt-1.5 text-xs text-gray-500">
                Admin ID is fixed to 1
              </p>
            </div>

            {/* Password */}
            <div>
              <label className="mb-2 block text-sm font-semibold text-gray-300">
                Password
              </label>

              <div className="relative">
                <LockKeyhole className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-500" />

                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  className="h-12 w-full rounded-xl border border-white/10 bg-black/30 pl-12 pr-12 text-white outline-none transition placeholder:text-gray-600 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
                  autoComplete="current-password"
                />

                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-2 text-gray-500 transition hover:text-white"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="mt-4 rounded-xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-400">
                {error}
              </div>
            )}

            {/* Login Button */}
            <button
              type="submit"
              disabled={loading}
              className="mt-6 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-red-600 to-orange-500 font-bold text-white shadow-lg shadow-red-900/30 transition hover:from-red-500 hover:to-orange-400 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? (
                <>
                  <span className="h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                  Logging in...
                </>
              ) : (
                <>
                  <LogIn className="h-5 w-5" />
                  Admin Login
                </>
              )}
            </button>
          </form>

          <div className="mt-6 border-t border-white/10 pt-5 text-center">
            <p className="text-xs text-gray-500">
              🔒 Authorized BPL administration only
            </p>
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-gray-600">
          © {new Date().getFullYear()} Badgaw Premier League
        </p>
      </div>
    </main>
  );
}

export default AdminLogin;